/**
 * ClientGalleryView — the public, openly-accessible client gallery page.
 *
 * Media Library V2 client sharing (docs/pm/active/media-library-client-sharing-v2.md).
 * A photographer shares a gallery link (https://{slug}.fuguelab.app/g/{token});
 * their client opens it with NO account. This is the shared viewer every site
 * mounts once:
 *
 *   import { ClientGalleryView } from '@gjl/site-kit'
 *   <Route path="/g/:token" element={<ClientGalleryView apiBase={CMS_API} orgSlug={SLUG} />} />
 *
 * It fetches the public REST share endpoint at runtime (shares are dynamic, so
 * they are NOT in the site's static published content) and renders a grid +
 * lightbox. Payload URLs are already client-ready (server-resolved).
 */
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'

interface ShareItem {
  id: string
  src: string
  alt?: string | null
  title?: string | null
  caption?: string | null
}

interface ShareResponse {
  share: { title: string | null; allowDownload: boolean; requiresPassword: boolean }
  gallery: { id: string; name: string; description: string | null } | null
  items: ShareItem[]
  locked: boolean
}

type Status = 'loading' | 'ready' | 'notfound' | 'error'

export interface ClientGalleryViewProps {
  /** CMS public API base, e.g. https://api.cms.fuguelab.app */
  apiBase: string
  /** This site's org slug. */
  orgSlug: string
  /** Optional brand accent (e.g. the site's primary color) for the download button. */
  accent?: string
}

export function ClientGalleryView({ apiBase, orgSlug, accent = '#111' }: ClientGalleryViewProps) {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<ShareResponse | null>(null)
  const [status, setStatus] = useState<Status>('loading')
  const [active, setActive] = useState<ShareItem | null>(null)

  useEffect(() => {
    if (!token) {
      setStatus('notfound')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(
          `${apiBase}/api/v1/public/${encodeURIComponent(orgSlug)}/shares/${encodeURIComponent(token)}`
        )
        if (res.status === 404) {
          if (!cancelled) setStatus('notfound')
          return
        }
        if (!res.ok) throw new Error(String(res.status))
        const json = (await res.json()) as ShareResponse
        if (!cancelled) {
          setData(json)
          setStatus('ready')
        }
      } catch {
        if (!cancelled) setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiBase, orgSlug, token])

  // Lightbox keyboard nav
  const items = useMemo(() => data?.items ?? [], [data])
  const move = useCallback(
    (dir: 1 | -1) => {
      setActive((cur) => {
        if (!cur) return cur
        const i = items.findIndex((x) => x.id === cur.id)
        const next = items[(i + dir + items.length) % items.length]
        return next ?? cur
      })
    },
    [items]
  )
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null)
      else if (e.key === 'ArrowRight') move(1)
      else if (e.key === 'ArrowLeft') move(-1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, move])

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: '#888' }}>Loading gallery…</div>
    )
  }
  if (status === 'notfound') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>This gallery isn’t available</h1>
          <p style={{ color: '#888' }}>The link may have expired or been removed.</p>
        </div>
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: '#b00' }}>
        Something went wrong loading this gallery.
      </div>
    )
  }
  if (data?.locked) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>{data.share.title || 'Protected gallery'}</h1>
          <p style={{ color: '#888' }}>This gallery is password protected.</p>
        </div>
      </div>
    )
  }

  const title = data?.share.title || data?.gallery?.name || 'Gallery'
  const allowDownload = data?.share.allowDownload

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 20px 80px' }}>
      <header style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h1>
        {data?.gallery?.description && <p style={{ color: '#666', marginTop: 8 }}>{data.gallery.description}</p>}
        <p style={{ color: '#999', marginTop: 6, fontSize: 14 }}>
          {items.length} {items.length === 1 ? 'photo' : 'photos'}
        </p>
      </header>

      {items.length === 0 ? (
        <p style={{ color: '#888' }}>No photos in this gallery yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setActive(it)}
              style={{
                padding: 0,
                border: 0,
                cursor: 'zoom-in',
                background: '#f2f2f2',
                aspectRatio: '1 / 1',
                overflow: 'hidden',
                borderRadius: 8,
              }}
              aria-label={it.alt || it.title || 'Open photo'}
            >
              <img
                src={it.src}
                alt={it.alt || ''}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      )}

      {active && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.92)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 1000,
            padding: 24,
          }}
        >
          {/* Full-screen backdrop button closes the lightbox (keyboard: Esc, handled above). */}
          <button
            type="button"
            aria-label="Close gallery"
            onClick={() => setActive(null)}
            style={{ position: 'absolute', inset: 0, border: 0, background: 'transparent', cursor: 'zoom-out' }}
          />
          <img
            src={active.src}
            alt={active.alt || ''}
            style={{
              position: 'relative',
              maxWidth: '92vw',
              maxHeight: '82vh',
              objectFit: 'contain',
              borderRadius: 4,
              pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'fixed', top: 16, right: 16, display: 'flex', gap: 10, zIndex: 1 }}>
            {allowDownload && (
              <a
                href={active.src}
                download
                style={{
                  background: accent,
                  color: '#fff',
                  padding: '8px 14px',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 14,
                }}
              >
                Download
              </a>
            )}
            <button
              type="button"
              onClick={() => setActive(null)}
              style={{
                background: '#fff',
                color: '#111',
                padding: '8px 14px',
                borderRadius: 6,
                border: 0,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
          {items.length > 1 && (
            <>
              <button type="button" onClick={() => move(-1)} aria-label="Previous" style={navBtn('left')}>
                ‹
              </button>
              <button type="button" onClick={() => move(1)} aria-label="Next" style={navBtn('right')}>
                ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function navBtn(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'fixed',
    top: '50%',
    [side]: 16,
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.14)',
    color: '#fff',
    border: 0,
    borderRadius: '50%',
    width: 44,
    height: 44,
    fontSize: 26,
    cursor: 'pointer',
  }
}

export default ClientGalleryView
